import { useState } from "react";
import { Play, Pause, Volume2, VolumeX, Music2, Maximize2, X, Volume1 } from "lucide-react";
import { useMusic } from "@/context/MusicContext";

function fmt(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

function Artwork({ artwork, size, playing, muted }) {
  return (
    <div className={`relative ${size} shrink-0 rounded-2xl overflow-hidden bg-gradient-to-br from-[#C89B55] to-[#8A6D2F] flex items-center justify-center`}>
      {artwork
        ? <img src={artwork} alt="Sisterhood cover art" className="h-full w-full object-cover" />
        : <Music2 className="text-[#291B2D]" size={22} />}
      {playing && !muted && (
        <span className="absolute bottom-1.5 right-1.5 flex items-end gap-[2px] h-3">
          <span className="w-[2px] bg-[#FCFAF6] animate-[sh-eq_0.9s_ease-in-out_infinite]" style={{ height: "60%" }} />
          <span className="w-[2px] bg-[#FCFAF6] animate-[sh-eq_0.9s_ease-in-out_infinite]" style={{ height: "100%", animationDelay: "0.15s" }} />
          <span className="w-[2px] bg-[#FCFAF6] animate-[sh-eq_0.9s_ease-in-out_infinite]" style={{ height: "40%", animationDelay: "0.3s" }} />
        </span>
      )}
    </div>
  );
}

// Subtle, brand-native "Now Playing" element for the Home screen, with a
// tap-to-expand full-screen player.
export default function NowPlaying() {
  const music = useMusic();
  const [expanded, setExpanded] = useState(false);
  if (!music) return null;
  const { track, playing, muted, volume, currentTime, duration, audible, toggle, toggleMute, seek, changeVolume, keepPlaying } = music;
  const pct = duration ? Math.min(100, (currentTime / duration) * 100) : 0;
  const volPct = Math.round((volume || 0) * 100);

  return (
    <>
      <div
        data-testid="home-now-playing"
        className="rounded-3xl border border-[#291B2D]/8 bg-white/70 backdrop-blur-sm p-4 lg:p-5 flex items-center gap-4 shadow-[0_4px_20px_rgba(41,27,45,0.04)]"
      >
        <button data-testid="home-now-playing-expand" onClick={() => setExpanded(true)} aria-label="Expand player" className="shrink-0 rounded-2xl">
          <Artwork artwork={track.artwork} size="h-14 w-14 lg:h-16 lg:w-16" playing={playing} muted={muted} />
        </button>

        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#C89B55] font-semibold">Now playing</p>
          <p data-testid="home-now-playing-title" className="font-display text-base lg:text-lg leading-tight truncate mt-0.5">{track.title}</p>
          {track.artist ? <p className="text-xs text-[#291B2D]/55 truncate">{track.artist}</p> : null}
          <div className="mt-2.5 flex items-center gap-2">
            <span className="text-[10px] tabular-nums text-[#291B2D]/45 w-8 text-right">{fmt(currentTime)}</span>
            <input
              data-testid="home-now-playing-seek"
              type="range" min={0} max={duration || 0} step="1" value={currentTime}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Track progress"
              className="flex-1 h-1 appearance-none rounded-full cursor-pointer accent-[#291B2D]"
              style={{ background: `linear-gradient(to right, #C89B55 ${pct}%, rgba(41,27,45,0.10) ${pct}%)` }}
            />
            <span className="text-[10px] tabular-nums text-[#291B2D]/45 w-8">{fmt(duration)}</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid="home-now-playing-expand-icon" onClick={() => setExpanded(true)} aria-label="Expand player"
            className="hidden sm:flex h-10 w-10 rounded-full items-center justify-center text-[#291B2D]/60 hover:text-[#291B2D] hover:bg-[#F7F2EA] transition-smooth">
            <Maximize2 size={16} />
          </button>
          <button data-testid="home-now-playing-mute" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}
            className="h-10 w-10 rounded-full flex items-center justify-center text-[#291B2D]/60 hover:text-[#291B2D] hover:bg-[#F7F2EA] transition-smooth">
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          {audible ? (
            <button data-testid="home-now-playing-toggle" onClick={toggle} aria-label="Pause music"
              className="h-11 w-11 rounded-full flex items-center justify-center bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] transition-smooth">
              <Pause size={17} fill="currentColor" />
            </button>
          ) : (
            <button data-testid="home-keep-playing" onClick={keepPlaying} aria-label="Keep playing music"
              className="inline-flex items-center gap-2 rounded-full bg-[#C89B55] text-[#291B2D] pl-3.5 pr-4 py-2.5 text-xs font-semibold tracking-wide hover:bg-[#B38743] transition-smooth shadow-[0_6px_20px_rgba(200,155,85,0.45)]">
              <Play size={14} fill="currentColor" /> Keep playing
            </button>
          )}
        </div>
      </div>

      {/* Expanded full-screen player */}
      {expanded && (
        <div
          data-testid="expanded-player"
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-6 py-10 animate-fade-in"
          style={{ background: "radial-gradient(120% 90% at 50% 0%, #4A2E4F 0%, #291B2D 55%, #1E1421 100%)" }}
        >
          <button data-testid="expanded-player-close" onClick={() => setExpanded(false)} aria-label="Close player"
            className="absolute top-5 right-5 h-11 w-11 rounded-full flex items-center justify-center text-[#FCFAF6]/70 hover:text-[#FCFAF6] hover:bg-white/10 transition-smooth">
            <X size={22} />
          </button>

          <div className="w-full max-w-sm flex flex-col items-center">
            <Artwork artwork={track.artwork} size="h-64 w-64 sm:h-72 sm:w-72 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.45)]" playing={playing} muted={muted} />

            <p className="mt-8 text-xs uppercase tracking-[0.24em] text-[#C89B55] font-semibold">Sisterhood+ signature</p>
            <h2 data-testid="expanded-player-title" className="mt-1.5 font-display text-3xl text-[#FCFAF6] text-center">{track.title}</h2>
            {track.artist ? <p className="text-sm text-[#FCFAF6]/55 mt-1">{track.artist}</p> : null}

            {/* Progress */}
            <div className="w-full mt-8">
              <input
                data-testid="expanded-player-seek"
                type="range" min={0} max={duration || 0} step="1" value={currentTime}
                onChange={(e) => seek(Number(e.target.value))}
                aria-label="Track progress"
                className="w-full h-1.5 appearance-none rounded-full cursor-pointer accent-[#C89B55]"
                style={{ background: `linear-gradient(to right, #C89B55 ${pct}%, rgba(252,250,246,0.18) ${pct}%)` }}
              />
              <div className="flex justify-between mt-2 text-[11px] tabular-nums text-[#FCFAF6]/50">
                <span>{fmt(currentTime)}</span><span>{fmt(duration)}</span>
              </div>
            </div>

            {/* Transport */}
            <div className="mt-8 flex items-center justify-center gap-6">
              <button data-testid="expanded-player-mute" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}
                className="h-12 w-12 rounded-full flex items-center justify-center text-[#FCFAF6]/70 hover:text-[#FCFAF6] hover:bg-white/10 transition-smooth">
                {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
              </button>
              <button data-testid="expanded-player-toggle" onClick={toggle} aria-label={playing ? "Pause music" : "Play music"}
                className="h-16 w-16 rounded-full flex items-center justify-center bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] transition-smooth shadow-[0_12px_36px_rgba(200,155,85,0.4)]">
                {playing ? <Pause size={26} fill="currentColor" /> : <Play size={26} fill="currentColor" className="ml-1" />}
              </button>
              <div className="h-12 w-12" />
            </div>

            {/* Volume slider */}
            <div className="mt-10 w-full flex items-center gap-3">
              <Volume1 size={18} className="text-[#FCFAF6]/50 shrink-0" />
              <input
                data-testid="expanded-player-volume"
                type="range" min={0} max={1} step="0.01" value={volume}
                onChange={(e) => changeVolume(Number(e.target.value))}
                aria-label="Volume"
                className="flex-1 h-1.5 appearance-none rounded-full cursor-pointer accent-[#C89B55]"
                style={{ background: `linear-gradient(to right, #FCFAF6 ${volPct}%, rgba(252,250,246,0.18) ${volPct}%)` }}
              />
              <Volume2 size={18} className="text-[#FCFAF6]/50 shrink-0" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
